import clsx from 'clsx'
import Script from 'next/script'
import { MouseEventHandler, VFC } from 'react'
import { ThemeContext } from 'shared-components/contexts/ThemeContext'
import {
  UIVariantContext,
  UIVariantContextProps,
} from 'shared-components/contexts/UIVariantContext'
import Linked from 'shared-components/service/Linked'
import AppImage from 'shared-components/ui/AppImage'
import type {
  BasicStyledComponent,
  DSGoogleButton,
  DSGoogleButtons,
  ThemeVariants,
} from 'shared-definitions/types'
import styles from './GoogleButtons.module.css'
import SplitterLine from './SplitterLine'

interface GoogleButtonsProps extends BasicStyledComponent {
  data: DSGoogleButtons | null
}

// CTA brand name derived from the app's UIVariant brand (the single source of truth already
// set per-app in Layout), not from string-matching the Google links, which would be brittle.
// Assumes that the app's UIVariantContext is set to either 'soundguys' or 'androidauthority' and that the Google buttons are only for those brands.
// If the app's UIVariantContext is appended to, this will need to be updated as well
const SITE_NAME: Record<UIVariantContextProps['colors'], string> = {
  soundguys: 'SoundGuys',
  androidauthority: 'Android Authority',
}

interface PreferredSourceApi {
  init: (opts: { theme?: 'light' | 'dark'; lang?: string }) => void
  addPreferredSource: () => void
}

// Google Preferred Sources SDK callback queue (loaded via publisher.js).
type PreferredSourceQueue = Array<(api: PreferredSourceApi) => void>

// Theme the SDK was last initialized with; re-init when the reader toggles theme so
// Google's dialog matches (null = not yet initialized).
let preferredSourceInitedTheme: ThemeVariants | null = null

// Trigger Google's Preferred Sources popup in-place instead of navigating to a new tab.
function openPreferredSource(theme: ThemeVariants): void {
  const w = window as Window & { PREFERRED_SOURCE?: PreferredSourceQueue }
  ;(w.PREFERRED_SOURCE = w.PREFERRED_SOURCE ?? []).push(api => {
    if (preferredSourceInitedTheme !== theme) {
      api.init({ theme })
      preferredSourceInitedTheme = theme
    }
    api.addPreferredSource()
  })
}

// Button labels are overridden in GoogleButtons to maintain consistency across apps
const GoogleButtons: VFC<GoogleButtonsProps> = ({ className, data }) => {
  const { theme } = ThemeContext.useContainer()
  const { colors } = UIVariantContext.useContainer()

  if (!data || (!data.preferredSource && !data.discover)) {
    return null
  }

  const renderButton = (
    button: DSGoogleButton | null,
    label: string,
    onClick?: MouseEventHandler<HTMLAnchorElement>
  ): JSX.Element | null => {
    if (!button) {
      return null
    }

    return (
      <Linked className={styles.button} onClick={onClick} {...button.link.pLink}>
        {button.logo ? (
          <AppImage image={button.logo} className={styles.logo} layout="raw" sizes={22} />
        ) : null}
        <span className={styles.label}>{label}</span>
      </Linked>
    )
  }

  // Keep the href as a no-JS fallback; when the SDK is available, open the popup in-place.
  const handlePreferredSource: MouseEventHandler<HTMLAnchorElement> = e => {
    e.preventDefault()
    openPreferredSource(theme)
  }

  return (
    <div className={styles.googleButtons}>
      <SplitterLine className={styles.splitter} />
      <div className={clsx(styles.container, className)}>
        <div className={styles.buttonCta}>{`Add ${SITE_NAME[colors]} on Google: `}</div>
        {data.preferredSource ? (
          <Script
            id="google-preferred-sources"
            src="https://news.google.com/swg/js/v1/publisher.js"
            strategy="lazyOnload"
            {...{ 'preferred-sources-control': 'manual' }}
          />
        ) : null}
        <div className={styles.buttons}>
          {renderButton(data.preferredSource, 'Preferred Source', handlePreferredSource)}
          {renderButton(data.discover, 'Google Discover')}
        </div>
      </div>
      <SplitterLine className={styles.splitter} />
    </div>
  )
}

export default GoogleButtons
